/**
 * BillingStatusIndicator
 * Displays plan status (active, trialing, past_due) with loading state.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { useBilling, getCurrentDiffDays } from "../../../hooks/useBilling";
import { Icon } from "../UIElements/Icon";
import { Skeleton } from "../Skeletons";

const BillingStatusIndicator = ({ className = "" }) => {
  const { t } = useTranslation(["billing"]);
  const {
    currentStatus,
    currentStatusUntil,
    trialDaysRemaining,
    isLoading,
    isError,
  } = useBilling();

  if (isLoading) {
    return (
      <div
        className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${className}`}
        aria-busy="true"
        aria-label={t("loading")}
      >
        <Skeleton className="h-4 w-20 rounded" />
      </div>
    );
  }

  if (isError || !currentStatus) {
    return null;
  }

  const statusConfig = {
    active: {
      labelKey: "projectActive",
      icon: "CheckCircle",
      className: "bg-[var(--success-bg)] text-success border-success/30",
    },
    trialing: {
      labelKey: "trialDaysRemaining",
      icon: "Clock",
      className: "bg-[var(--warning-bg)] text-warning border-warning/30",
    },
    past_due: {
      labelKey: "projectPastDue",
      icon: "AlertCircle",
      className: "bg-[var(--error-bg)] text-destructive border-destructive/30",
    },
    incomplete: {
      labelKey: "incomplete",
      icon: "AlertCircle",
      className: "bg-[var(--warning-bg)] text-warning border-warning/30",
    },
    suspended: {
      labelKey: "suspended",
      icon: "AlertTriangle",
      className: "bg-[var(--error-bg)] text-destructive border-destructive/30",
    },
    canceled: {
      labelKey: "canceled",
      icon: "XCircle",
      className: "bg-muted text-muted-foreground border-border",
    },
  };

  const config = statusConfig[currentStatus] || {
    labelKey: "unknown",
    icon: "HelpCircle",
    className: "bg-muted text-muted-foreground border-border",
  };

  const daysUntil =
    currentStatus === "trialing" && trialDaysRemaining != null
      ? trialDaysRemaining
      : currentStatus === "past_due" && currentStatusUntil
        ? getCurrentDiffDays(currentStatusUntil)
        : null;

  const label =
    config.labelKey === "trialDaysRemaining" && daysUntil != null
      ? daysUntil >= 0
        ? t(config.labelKey, { count: daysUntil })
        : t("trialExpiredDaysAgo", { count: Math.abs(daysUntil) })
      : config.labelKey === "trialDaysRemaining" && daysUntil == null
        ? t("trial")
        : config.labelKey === "projectPastDue" && daysUntil != null
          ? daysUntil >= 0
            ? t("pastDueDaysRemaining", { count: daysUntil })
            : t("pastDueOverdueDaysAgo", { count: Math.abs(daysUntil) })
          : t(config.labelKey);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium ${config.className} ${className}`}
      role="status"
      aria-live="polite"
    >
      <Icon name={config.icon} className="h-3.5 w-3.5 flex-shrink-0" />
      <span>{label}</span>
    </span>
  );
};

export default BillingStatusIndicator;
